import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { GhostIcon, PumpkinIcon } from './HalloweekLink';
import { BatIcon } from './BatIcon';
type Kind = 'ghost' | 'pumpkin' | 'bat';
type Side = 'left' | 'right';
interface Peeker {
  id: number;
  kind: Kind;
  side: Side;
  top: number;
  tilt: number;
  line: string;
}
interface HalloweekPeekersProps {
  onOpen: () => void;
}
const SLOTS: {
  side: Side;
  top: number;
}[] = [{
  side: 'left',
  top: 50
}, {
  side: 'right',
  top: 100
}, {
  side: 'left',
  top: 220
}, {
  side: 'right',
  top: 270
}, {
  side: 'left',
  top: 390
}, {
  side: 'right',
  top: 430
}];

// Each figure has its own voice
const LINES: Record<Kind, string[]> = {
  ghost: ['Buuu!', 'Klicka mig!', 'Vågar du?', 'Jag spökar här…', 'Fånga mig!'],
  pumpkin: ['Halloweek →', 'Kolla schemat!', 'Pinsa & AW?', 'Bus eller godis?', 'Boka plats!'],
  bat: ['Flaxa hit!', 'Mordmysterium?', 'Monsterdisko!', 'Vecka 44!', 'Klicka då!']
};
const KINDS: Kind[] = ['ghost', 'pumpkin', 'bat'];
const ease = [0.23, 1, 0.32, 1] as const;
const VISIBLE_MS = 4200;
const SPAWN_MS = 1600;
const BUBBLE_DELAY_MS = 450;
export function HalloweekPeekers({
  onOpen
}: HalloweekPeekersProps) {
  const reduceMotion = useReducedMotion();
  const [peekers, setPeekers] = useState<Peeker[]>([]);
  const [bubbles, setBubbles] = useState<Set<number>>(new Set());
  const [caught, setCaught] = useState<number | null>(null);
  const nextId = useRef(0);
  useEffect(() => {
    if (reduceMotion) return;
    const timeouts: number[] = [];
    const maxOnScreen = window.innerWidth < 768 ? 2 : 3;
    const spawn = () => {
      setPeekers(current => {
        if (current.length >= maxOnScreen) return current;
        const taken = new Set(current.map(p => `${p.side}-${p.top}`));
        const free = SLOTS.filter(s => !taken.has(`${s.side}-${s.top}`));
        if (!free.length) return current;
        const slot = free[Math.floor(Math.random() * free.length)];
        const id = nextId.current++;
        const kind = KINDS[Math.floor(Math.random() * KINDS.length)];
        const lines = LINES[kind];
        const peeker: Peeker = {
          id,
          ...slot,
          kind,
          tilt: Math.round(Math.random() * 16 - 8),
          line: lines[Math.floor(Math.random() * lines.length)]
        };
        // Bubble pops up on its own – works on touch screens too
        timeouts.push(window.setTimeout(() => setBubbles(b => new Set(b).add(id)), BUBBLE_DELAY_MS));
        timeouts.push(window.setTimeout(() => {
          setPeekers(list => list.filter(p => p.id !== id));
          setBubbles(b => {
            const next = new Set(b);
            next.delete(id);
            return next;
          });
        }, VISIBLE_MS));
        return [...current, peeker];
      });
    };
    timeouts.push(window.setTimeout(spawn, 700));
    const interval = window.setInterval(spawn, SPAWN_MS);
    return () => {
      window.clearInterval(interval);
      timeouts.forEach(t => window.clearTimeout(t));
    };
  }, [reduceMotion]);
  const handleCatch = (id: number) => {
    setCaught(id);
    window.setTimeout(onOpen, 240);
  };

  // Reduced motion: one calm ghost with its bubble always visible
  if (reduceMotion) {
    return <div className="pointer-events-none absolute inset-x-0 top-0 h-[540px] z-20" data-id="element-626" data-mp="jwpgsqmhbaa7">
        <button onClick={onOpen} className="pointer-events-auto absolute right-2 md:right-[6%] top-12 flex flex-col items-center" aria-label="Till Halloweek" data-id="element-627" data-mp="jubm4ivpo036">
          <span className="mb-2 whitespace-nowrap rounded-2xl bg-[#0E0C0B] px-3.5 py-1.5 text-sm font-bold text-[#FF9A4D]" data-id="element-628" data-mp="squd3pkdtdx4">
            Halloweek →
          </span>
          <GhostIcon className="w-16 h-16 md:w-20 md:h-20" data-id="element-629" data-mp="7dw647ystso" />
        </button>
      </div>;
  }
  return <div className="pointer-events-none absolute inset-x-0 top-0 h-[540px] z-20 overflow-hidden" data-id="element-630" data-mp="3sbzoce9517pd">
      <AnimatePresence data-id="element-631" data-mp="8xxg41erl5zh">
        {peekers.map(p => {
        const fromX = p.side === 'left' ? -120 : 120;
        const isCaught = caught === p.id;
        const showBubble = bubbles.has(p.id) && !isCaught;
        return <motion.button key={p.id} type="button" onClick={() => handleCatch(p.id)} aria-label={`${p.line} – till Halloweek`} initial={{
          x: fromX,
          opacity: 0,
          rotate: p.tilt * 3,
          scale: 0.96
        }} animate={isCaught ? {
          scale: 1.5,
          opacity: 0,
          rotate: p.side === 'left' ? -20 : 20,
          transition: {
            duration: 0.22,
            ease
          }
        } : {
          x: 0,
          opacity: 1,
          rotate: p.tilt,
          scale: 1,
          transition: {
            type: 'spring',
            stiffness: 260,
            damping: 16
          }
        }} exit={{
          x: fromX,
          opacity: 0,
          rotate: p.tilt * 3,
          transition: {
            duration: 0.25,
            ease
          }
        }} whileHover={{
          scale: 1.12,
          rotate: 0
        }} whileTap={{
          scale: 0.9
        }} style={{
          top: p.top
        }} className={`pointer-events-auto absolute cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF7A1A] rounded-full ${p.side === 'left' ? 'left-0 sm:left-2 md:left-[4%] lg:left-[8%]' : 'right-0 sm:right-2 md:right-[4%] lg:right-[8%]'}`} data-id="element-632" data-mp="nl8nrs7ts5ub">
              <Figure kind={p.kind} data-id="element-633" data-mp="34vwhy96hd8w6" />

              <AnimatePresence data-id="element-634" data-mp="1ydq71h2jj57l">
                {showBubble && <motion.span initial={{
              opacity: 0,
              scale: 0.96,
              y: 8
            }} animate={{
              opacity: 1,
              scale: 1,
              y: 0,
              transition: {
                type: 'spring',
                stiffness: 420,
                damping: 18
              }
            }} exit={{
              opacity: 0,
              scale: 0.96,
              y: 6,
              transition: {
                duration: 0.15,
                ease
              }
            }} className={`absolute -top-11 md:-top-12 whitespace-nowrap rounded-2xl bg-[#0E0C0B] px-3.5 py-1.5 md:px-4 md:py-2 text-sm md:text-base font-bold text-[#FF9A4D] shadow-[0_6px_16px_rgba(0,0,0,0.25)] ${p.side === 'left' ? 'left-1/3 rounded-bl-sm' : 'right-1/3 rounded-br-sm'}`} data-id="element-635" data-mp="1tedbm7xlspte">
                    {p.line}
                  </motion.span>}
              </AnimatePresence>
            </motion.button>;
      })}
      </AnimatePresence>
    </div>;
}
function Figure({
  kind
}: {
  kind: Kind;
}) {
  if (kind === 'ghost') {
    // Floats and sways, with a faint flicker
    return <motion.span className="block drop-shadow-[0_8px_12px_rgba(0,0,0,0.2)]" animate={{
      y: [0, -10, 0],
      rotate: [0, -4, 4, 0],
      opacity: [1, 0.85, 1]
    }} transition={{
      duration: 2.2,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-636" data-mp="30ai9lmkzrcye">
        <GhostIcon className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24" data-id="element-637" data-mp="3aaiuve2f8358" />
      </motion.span>;
  }
  if (kind === 'pumpkin') {
    // Bounces and squashes
    return <motion.span className="block origin-bottom drop-shadow-[0_8px_12px_rgba(0,0,0,0.2)]" animate={{
      y: [0, -8, 0],
      scaleY: [1, 0.94, 1.03, 1],
      scaleX: [1, 1.05, 0.98, 1]
    }} transition={{
      duration: 1.4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-638" data-mp="3j8kubp6ianqe">
        <PumpkinIcon className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20" data-id="element-639" data-mp="3nzui0k8hvgzn" />
      </motion.span>;
  }
  // Bat: flaps its wings and zig-zags
  return <motion.span className="block drop-shadow-[0_8px_12px_rgba(0,0,0,0.2)]" animate={{
    y: [0, -12, 4, 0],
    x: [0, 6, -6, 0]
  }} transition={{
    duration: 1.6,
    repeat: Infinity,
    ease: 'easeInOut'
  }} data-id="element-640" data-mp="6sh7fwkh0jty">
      <motion.span className="block" animate={{
      scaleY: [1, 0.6, 1]
    }} transition={{
      duration: 0.35,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-641" data-mp="2iokab0mw0iz7">
        <BatIcon className="w-16 h-10 sm:w-20 sm:h-12 md:w-24 md:h-14" data-id="element-642" data-mp="2sl5ifuu7ea79" />
      </motion.span>
    </motion.span>;
}